import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GlassPage } from '../components/chrome';
import { PageHero } from '../components/ui';
import { JOB_CATEGORIES } from '../lib/content';
import { postJSON } from '../lib/api';

const Careers = () => {
  const [formData, setFormData] = useState({
    job: '',
    name: '',
    email: '',
    message: ''
  });
  const formRef = useRef(null);

  // Lenis owns the scroll position, so a plain scrollIntoView gets reverted.
  const handleApplyClick = (jobTitle) => {
    setFormData(prev => ({ ...prev, job: jobTitle }));
    const el = formRef.current;
    if (!el) return;
    document.getElementById('apply-name')?.focus({ preventScroll: true });
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [messageError, setMessageError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.message.length < 100) {
      setMessageError(`Your message needs at least 100 characters. It has ${formData.message.length}.`);
      document.getElementById('apply-message')?.focus();
      return;
    }
    setMessageError('');
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await postJSON('/careers', {
        job: formData.job,
        name: formData.name,
        email: formData.email,
        message: formData.message || null,
      });
      setSubmitStatus('success');
      setFormData({ job: '', name: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClass = 'glass-input w-full px-5 py-3.5';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-widest mb-2 text-zinc-500';

  return (
    <GlassPage>
      <PageHero
        title={<>Join Our<br/>Team</>}
        lead="Explore open positions by category and help us build the future of assistive technology."
      />

      {/* Job Listings */}
      <section className="px-4 md:px-6 pb-12 md:pb-16">
        <div className="max-w-[1600px] mx-auto">
          {JOB_CATEGORIES.map((category) => (
            <div key={category.category} className="mb-10 md:mb-16 last:mb-0">
              <h2 className="text-xl md:text-3xl font-bold tracking-tighter uppercase mb-4 md:mb-8 text-zinc-900 px-1 md:px-2">
                {category.category}
              </h2>
              <div className="space-y-3 md:space-y-4">
                {category.jobs.map((job) => (
                  <div key={job.title} className="glass p-5 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg md:text-2xl font-bold tracking-tight mb-1.5 md:mb-2 text-zinc-900">{job.title}</h3>
                        <p className="text-sm text-zinc-600">{job.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleApplyClick(job.title)}
                        aria-label={`Apply now for ${job.title}`}
                        className="btn-dark w-full md:w-auto px-6 py-3 md:self-center whitespace-nowrap"
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section ref={formRef} className="px-4 md:px-6 pb-12 md:pb-16 scroll-mt-20">
        <div className="glass-strong max-w-3xl mx-auto px-5 sm:px-8 md:px-12 py-10 md:py-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase mb-3 md:mb-4 text-zinc-900">
            Apply for a role
          </h2>
          <p className="text-zinc-600 mb-8 md:mb-12">
            Interested in a position? Fill out the form below and we'll get back to you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="apply-job" className={labelClass}>Job Position</label>
              <select
                id="apply-job"
                name="job"
                value={formData.job}
                onChange={handleChange}
                required
                className={`${inputClass} appearance-none cursor-pointer`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2318181b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1.25rem center'
                }}
              >
                <option value="">Select a position</option>
                {JOB_CATEGORIES.flatMap((cat) =>
                  cat.jobs.map((job) => (
                    <option key={job.title} value={job.title}>{job.title}</option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label htmlFor="apply-name" className={labelClass}>Name</label>
              <input
                id="apply-name"
                autoComplete="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="apply-email" className={labelClass}>Email</label>
              <input
                id="apply-email"
                autoComplete="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClass}
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="apply-message" className={labelClass}>Message (min. 100 characters)</label>
              <textarea
                id="apply-message"
                aria-invalid={!!messageError}
                aria-describedby={messageError ? 'apply-message-count apply-message-error' : 'apply-message-count'}
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                minLength={100}
                rows={6}
                className={`${inputClass} resize-none`}
                placeholder="Tell us why you're interested in this position..."
              />
              <p id="apply-message-count" className="text-xs text-zinc-500 mt-2">
                {formData.message.length} / 100 characters
              </p>
              {messageError && (
                <p id="apply-message-error" role="alert" className="mt-2 text-sm font-bold text-red-700">Error: {messageError}</p>
              )}
            </div>

            <p className="text-sm text-zinc-600">
              We use your application to consider you for this role. See our <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
            </p>

            <button type="submit" disabled={isSubmitting} className={`btn-dark w-full md:w-auto md:px-12 ${isSubmitting ? 'opacity-60 cursor-wait' : ''}`}>
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>

            {submitStatus === 'success' && (
              <p role="status" className="text-sm text-green-800 font-bold">
                Application sent. We'll be in touch.
              </p>
            )}
            {submitStatus === 'error' && (
              <p role="alert" className="text-sm text-red-700 font-bold">
                Error: your application was not sent. Please try again or email careers@korsa.tech.
              </p>
            )}
          </form>
        </div>
      </section>
    </GlassPage>
  );
};

export default Careers;
